import { ClockImpactType } from '@sportlogiq/main';
import { NewPlayerEvent, PlayerEventName, PlayerEventType } from '@sportlogiq/main/core/games/player-event.types';
import { SeasonStageType } from '@sportlogiq/main/core/store/season/season.types';

export interface GameEventDefinition {
    id: string;
    label: string;
    name: string;
    type: string;
    clockImpact: ClockImpactType;
    playImpact: string;
}

export interface GameEventV3APIModel {
    id: string;
    gameId: string;
    teamId: string;
    playerId: string;
    secondaryPlayerId?: string | null;
    period: number;
    frame: number;
    defId: string;
    periodTime: number;
    gameTime: number;
    updatedOn: string;
}

export interface GameEventsQueryParams {
    gameeventid?: string[];
    gameeventname?: string[];
    gameid?: string[];
    leagueid?: string[];
    teamid?: string[];
    seasonid?: string[];
    seasonstage?: SeasonStageType[];
    playerid?: string;
    period?: number[];
    includeoutofsequenceevents?: boolean;
}

export interface FlagEventDefinition {
    id: string;
    label: string;
    name: string;
    requiresPlayersInvolved: boolean;
}

export interface PlayerEventDefinition {
    id: string;
    label: string;
    name: PlayerEventName;
    type: PlayerEventType;
    outcome: string;
    zone: string;
}

export interface ShiftEventDefinition {
    id: string;
    name: string;
    shorthand: string;
}

export interface ShiftEventV3APIModel {
    id: string;
    playerId: string;
    gameId: string;
    teamId: string;
    period: number;
    playerShiftEventDefinitionId: string;
    frame: number;
    createdBy: string;
    createdOn: string;
    linkedShiftEventId: string | null;
}

export interface EventsTimelineRequestParamsApi {
    from: number;
    to: number;
    withgameeventdefinitions?: boolean;
    withplayershifteventdefinitions?: boolean;
}

export interface EventsTimelineInitialStatePlayerApi {
    playerId: string;
    teamId: string;
    // the frame where this player's shift started
    shiftStartFrame: number;
    // the length of time the player has been on ice before the start frame requested in seconds
    shiftLength: number;
}

export interface EventsTimelineItemApi {
    gameEventIds?: string[];
    playerShiftEventIds?: string[];
}

export interface EventsTimelineResponseApi {
    // the initial state of the players on ice at the start frame requested
    initialState: {
        players: EventsTimelineInitialStatePlayerApi[];
        // the last game event with a valid clock impact that occured before the requested start frame
        lastClockImpactEventId: string | null;
    };
    // keyed by frame number as a string, gives the game and player shifts events that occur on various frames
    // not every frame in the video will be reprented, only the ones that have relevant events
    timeline: Record<string, EventsTimelineItemApi>;
    // game events keyed by their ids
    gameEvents: Record<string, GameEventV3APIModel>;
    // shift events keyed by their ids
    playerShiftEvents: Record<string, ShiftEventV3APIModel>;
    // game event definitions keyed by their ids
    gameEventDefinitions: Record<string, GameEventDefinition> | null;
    // shift event definitions keyed by their ids
    playerShiftEventDefinitions: Record<string, ShiftEventDefinition> | null;
}

export interface EventStateModel {
    gameEventDefinitions: Record<string, GameEventDefinition>;
    playerEventDefinitions: Record<string, PlayerEventDefinition>;
    contextPlayerEvents: Record<string, NewPlayerEvent>;
    flagEventDefinitions: Record<string, FlagEventDefinition>;
    shiftEventDefinitions: Record<string, ShiftEventDefinition>;
}

export const eventStateDefaults: EventStateModel = Object.freeze({
    gameEventDefinitions: undefined,
    playerEventDefinitions: undefined,
    contextPlayerEvents: undefined,
    flagEventDefinitions: undefined,
    shiftEventDefinitions: {}
});
